import styled, { keyframes } from 'styled-components';

export const CalendarStyle = styled.div<{ loading?: boolean; view: 'list' | 'grid' }>`
    ${({ view }) => view === 'list' && 'max-width: 800px;'}

    animation: ${({ loading }) => (loading ? LoadingAnim : 'none')} 2s infinite;
    pointer-events: ${({ loading }) => (loading ? 'none' : 'auto')};

    flex: 1;

    :root {
        --fc-border-color: ${({ theme }) => theme.color.grey2};
    }

    .fc-more-link {
        color: ${({ theme }) => theme.color.primary};
        padding: ${({ theme }) => theme.space.xs}px ${({ theme }) => theme.space.sm}px
            ${({ theme }) => theme.space.xs}px ${({ theme }) => theme.space.xs}px;
        border-radius: ${({ theme }) => theme.size.xs}px;
        background-color: ${({ theme }) => theme.color.white};
        border: 1px solid ${({ theme }) => theme.color.primary};
        display: block;
        position: relative;
        z-index: 3;

        &.fc-daygrid-more-link {
            margin-bottom: 10px;
        }

        &:hover {
            background-color: ${({ theme }) => theme.color.primary};
            color: ${({ theme }) => theme.color.white};
            text-decoration: none;
        }

        &.fc-timegrid-more-link {
            padding: ${({ theme }) => theme.space.xs}px ${({ theme }) => theme.space.sm}px;
            background-color: ${({ theme }) => theme.color.white};
            border: 1px solid ${({ theme }) => theme.color.grey3};
            border-radius: ${({ theme }) => theme.size.sm}px;
            color: ${({ theme }) => theme.font.color};

            &:hover {
                background-color: ${({ theme }) => theme.color.grey0};
                color: ${({ theme }) => theme.font.color};
            }
        }
    }

    .fc-more-popover {
        border: 1px solid ${({ theme }) => theme.color.grey2};
        overflow: hidden;
        box-shadow: rgba(0, 0, 0, 0.1) 2px 2px 5px;
        border-radius: 0 ${({ theme }) => theme.size.sm}px ${({ theme }) => theme.size.sm}px
            ${({ theme }) => theme.size.sm}px;

        .fc-popover-header {
            background-color: ${({ theme }) => theme.color.grey0};

            .fc-popover-title {
                font-weight: ${({ theme }) => theme.font.weight.medium};
            }

            .fc-popover-close {
                margin-top: -10px;
            }
        }
    }

    .fc-col-header-cell-cushion,
    .fc-daygrid-day-number {
        pointer-events: none;
    }

    .fc-scrollgrid-section-header {
        .fc-scroller {
            overflow: hidden !important;
            background-color: ${({ theme }) => theme.color.grey1};
        }
    }

    .fc-scrollgrid {
        border: 1px solid ${({ theme }) => theme.color.grey2};
        border-radius: ${({ theme }) => theme.size.sm}px;
        overflow: hidden;

        td:first-of-type,
        th:first-of-type {
            border-bottom: none;
            border-right: none;
        }

        .fc-col-header-cell {
            background-color: ${(props) => props.theme.color.grey1};
            padding: ${({ theme }) => theme.space.sm}px;
            border-bottom: none;

            & > div {
                text-align: center;
            }

            a {
                text-decoration: none;
                font-weight: ${({ theme }) => theme.font.weight.medium};
                text-transform: capitalize;
            }
        }

        .fc-day {
            height: 1px; // Redistributes the height more evenly between the day cells

            .fc-daygrid-day-bottom {
                margin-top: 10px !important;
            }

            &.fc-day-today {
                background-color: ${({ theme }) => theme.color.white};

                .fc-daygrid-day-number {
                    background-color: ${({ theme }) => theme.color.primary};
                    color: ${({ theme }) => theme.color.white};
                    font-weight: ${({ theme }) => theme.font.weight.bold};
                }
            }

            &.fc-day-other {
                background-color: ${({ theme }) => theme.color.grey0};
            }

            .fc-daygrid-day-number {
                font-weight: ${({ theme }) => theme.font.weight.normal};
                text-decoration: none;
                padding: ${({ theme }) => theme.space.xs}px ${({ theme }) => theme.space.sm}px
                    ${({ theme }) => theme.space.xs}px 12px;
                border-bottom-left-radius: ${({ theme }) => theme.size.md}px;
                margin-bottom: ${({ theme }) => theme.space.xs}px;
            }

            .fc-daygrid-event-harness {
                margin-top: 2px !important;
            }

            .fc-event {
                background-color: transparent !important;
                background: transparent !important;

                &:hover {
                    background-color: transparent !important;
                    background: none !important;
                }
            }
        }
    }

    .fc-timeGridWeek-view {
        .fc-timegrid-axis {
            border-bottom: none;
        }

        .fc-day-today {
            &.fc-col-header-cell {
                background-color: ${({ theme }) => theme.color.grey0} !important;

                .fc-col-header-cell-cushion {
                    color: ${({ theme }) => theme.color.primary};
                    font-weight: ${({ theme }) => theme.font.weight.semiBold};
                }
            }
        }

        .fc-timegrid-slots
            > table:nth-child(1)
            > tbody:nth-child(2)
            > tr:nth-child(1)
            > td:nth-child(1)
            > div:nth-child(1)
            > div:nth-child(1) {
            display: none;
        }

        .fc-timegrid-slot-label-cushion {
            transform: translateY(-13px);
            background-color: white;
            padding: 0 4px !important;
            font-size: 10px;
        }

        .fc-scrollgrid-section:not(.fc-scrollgrid-section-liquid) {
            .fc-scroller {
                overflow: hidden !important;
            }
        }

        .fc-timegrid-event-harness {
            position: absolute;

            .fc-event-main {
                color: initial;
            }

            a {
                overflow: hidden;
                box-shadow: none;

                &:hover {
                    background-color: transparent !important;
                    border-radius: 0 !important;
                }
            }
        }
    }

    .fc-timeGridDay-view {
        .fc-day-today {
            &.fc-col-header-cell {
                background-color: ${({ theme }) => theme.color.grey1} !important;
            }

            .fc-col-header-cell-cushion {
                color: ${({ theme }) => theme.color.primary};
                font-weight: ${({ theme }) => theme.font.weight.semiBold};
            }
        }

        .fc-timegrid-axis {
            border-bottom: none;
        }

        .fc-timegrid-slots
            > table:nth-child(1)
            > tbody:nth-child(2)
            > tr:nth-child(1)
            > td:nth-child(1)
            > div:nth-child(1)
            > div:nth-child(1) {
            display: none;
        }

        .fc-timegrid-slot-label-cushion {
            transform: translateY(-13px);
            background-color: white;
            padding: 0 4px !important;
            font-size: 10px;
        }

        .fc-scrollgrid-section:not(.fc-scrollgrid-section-liquid) {
            .fc-scroller {
                overflow: hidden !important;
            }
        }

        .fc-timegrid-event-harness {
            position: absolute;

            .fc-event-main {
                color: initial;
            }

            a {
                overflow: hidden;
                box-shadow: none;

                &:hover {
                    background-color: transparent !important;
                    border-radius: 0 !important;
                }
            }
        }
    }

    .fc-list {
        border-radius: ${({ theme }) => theme.size.sm}px;
        overflow: hidden;

        .fc-list-table {
            tr:last-of-type {
                margin-bottom: 20px;
            }
        }

        .fc-list-day {
            position: relative;
            z-index: 2;

            * {
                background-color: ${({ theme }) => theme.color.grey1};
            }

            th {
                border-top: none;
                border-bottom: none;
            }

            &.fc-day-today {
                .fc-list-day-cushion a {
                    color: ${({ theme }) => theme.color.white};
                    font-weight: ${({ theme }) => theme.font.weight.medium};
                }

                * {
                    background-color: ${({ theme }) => theme.color.primary};
                }
            }

            .fc-list-day-cushion a {
                padding: ${({ theme }) => theme.space.xs}px;
                pointer-events: none;
                font-weight: ${({ theme }) => theme.font.weight.normal};
                text-transform: capitalize;
            }
        }

        .fc-list-event-time {
            display: none;
        }

        .fc-list-event-graphic {
            display: none;
        }

        .fc-list-event-title {
            display: block;
            border: none;
            padding: 0px 14px;
            margin-top: 8px;
        }

        tr.fc-list-day:nth-child(1) {
            * {
                top: 0;
            }

            .fc-list-day-cushion {
                border-top: none;
            }
        }

        tr.fc-list-day {
            * {
                top: -20px;
            }

            .fc-list-day-cushion {
                border-top: 20px solid ${({ theme }) => theme.color.white};
            }
        }

        .fc-list-event:hover td {
            background-color: transparent;
        }

        .fc-list-empty {
            background-color: ${({ theme }) => theme.color.grey0};
        }
    }
`;

const LoadingAnim = keyframes`
    0%, 100% {
        opacity: 0.4;
    }
    50% {
        opacity: 0.8;
    }
`;
